import { renderFaceThumbnail } from '@src/utility/faceThumbnailRenderer';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const thumbnailCache = new Map<string, string>();

type Props = {
  videoUrl: string | undefined;
  time: number;
  box: [number, number, number, number];
  size?: number;
  cacheKey: string;
  label: string;
};

const FaceThumbnail = ({
  videoUrl,
  time,
  box,
  size = 64,
  cacheKey,
  label,
}: Props) => {
  const [dataUrl, setDataUrl] = useState<string | null>(
    () => thumbnailCache.get(cacheKey) ?? null,
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!videoUrl) {
      setError(true);
      console.warn('FaceThumbnail: no video URL provided');
      return;
    }
    if (thumbnailCache.has(cacheKey)) {
      setDataUrl(thumbnailCache.get(cacheKey) ?? null);
      return;
    }

    const controller = new AbortController();
    setError(false);
    renderFaceThumbnail({
      videoUrl,
      time,
      box,
      size,
      signal: controller.signal,
    })
      .then(url => {
        if (controller.signal.aborted) return;
        thumbnailCache.set(cacheKey, url);
        setDataUrl(url);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        if (err instanceof Error && err.message === 'aborted') return;
        console.warn('FaceThumbnail: failed to render face thumbnail');
        setError(true);
      });

    return () => {
      controller.abort();
    };
  }, [videoUrl, time, box, size, cacheKey]);

  if (error || !videoUrl) {
    return (
      <Placeholder $size={size} title={`${label} (no preview available)`}>
        {label}
      </Placeholder>
    );
  }

  if (!dataUrl) {
    return <Skeleton $size={size} aria-label="Loading face thumbnail" />;
  }

  return <Image $size={size} src={dataUrl} alt={label} />;
};

export default FaceThumbnail;

const Skeleton = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  animation: pulse 1.2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const Image = styled.img<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid var(--border);
`;

const Placeholder = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: var(--text-muted);
  text-align: center;
  padding: 4px;
`;
